import { FilterOperator } from '@jusbrasil-web/shared-apollo-client/types'

import { SerpQueryParam } from '../query-params'
import {
  expandCourts,
  expandJurisTypes,
  expandMotionType,
  findJournalState,
} from '../utils/enum-handler'
import { CommomFiltersProps, FiltersByKind, HaystackFilterProp } from './types'

// ref: https://github.com/jusbrasil/finder/blob/master/src/utils/haystack-filters/kind.js

const doctrineFilters = (params: CommomFiltersProps): HaystackFilterProp[] => {
  const filters: HaystackFilterProp[] = []

  if (params[SerpQueryParam.DoctrineType]?.length) {
    filters.push({
      field: 'type',
      operator: FilterOperator.In,
      value: params[SerpQueryParam.DoctrineType],
    })
  }

  if (params[SerpQueryParam.DoctrineLawArea]?.length) {
    filters.push({
      field: 'tags.name',
      operator: FilterOperator.In,
      value: params[SerpQueryParam.DoctrineLawArea],
    })
  }

  if (params[SerpQueryParam.IdDoutrina]) {
    filters.push({
      field: 'parent.id',
      operator: FilterOperator.Equals,
      value: params[SerpQueryParam.IdDoutrina],
    })
  }

  return filters
}

const jurisprudenceFilters = (params: CommomFiltersProps): HaystackFilterProp[] => {
  const filters: HaystackFilterProp[] = []

  if (params.jurisType) {
    const types = expandJurisTypes([params.jurisType])
    if (types.length > 0) {
      filters.push({
        field: 'type',
        operator: FilterOperator.In,
        value: types,
      })
    }
  }

  if (params.tribunal?.length) {
    const mainCourts = expandCourts(params.tribunal)
    if (mainCourts.length > 0) {
      filters.push({
        field: 'court',
        operator: FilterOperator.In,
        value: mainCourts,
      })
    }
  }

  if (params[SerpQueryParam.Rapporteur]) {
    filters.push({
      field: 'related_judges',
      operator: FilterOperator.Or,
      value: params[SerpQueryParam.Rapporteur].map((item) => ({
        field: 'related_judges',
        operator: FilterOperator.Matchexact,
        value: item,
      })),
    })
  }

  return filters
}

const motionFilters = (params: CommomFiltersProps): HaystackFilterProp[] => {
  const filters: HaystackFilterProp[] = []

  const type = expandMotionType(params.tipopeca || '')
  if (type) {
    filters.push({
      field: 'type',
      operator: FilterOperator.Equals,
      value: type,
    })
  }

  if (params.tribunal?.length) {
    const mainCourts = expandCourts(params.tribunal)
    if (mainCourts.length > 0) {
      filters.push({
        field: 'court',
        operator: FilterOperator.In,
        value: mainCourts,
      })
    }
  }

  return filters
}

const journalFilters = (params: CommomFiltersProps) => {
  const {
    [SerpQueryParam.State]: journalState, //
    [SerpQueryParam.JournalType]: journalType,
  } = params

  const filters: HaystackFilterProp[] = []

  if (journalState) {
    const validStates = journalState.filter((uf) => !!findJournalState(uf))
    if (validStates.length > 0) {
      filters.push({
        field: 'state',
        operator: FilterOperator.In,
        value: validStates.map((state) => state.toUpperCase()),
      })
    }
  }

  if (journalType) {
    filters.push({
      field: 'type',
      operator: FilterOperator.Equals,
      value: journalType.toUpperCase(),
    })
  }

  return filters
}

const filtersByKind: FiltersByKind = {
  DOCTRINE: doctrineFilters,
  JURISPRUDENCE: jurisprudenceFilters,
  MOTION: motionFilters,
  JOURNAL: journalFilters,
}

export const kindFilters = (
  kinds: string[] = [],
  params: CommomFiltersProps
): HaystackFilterProp[] =>
  kinds.flatMap((kind) => (filtersByKind[kind] ? filtersByKind[kind](params) : []))
